<template lang="pug">
  search-widget(
    :app-code='l10n("app.code")'
    :showCounts='showCounts'
    :showMap='showMap'
    :title='"Search " + l10n("app.name")'
    advanced-search-url='/recorder/FindRecordedDocuments'
    county-detail-url='/recorderCounties.jsp')
    template(slot='before')
      slot(name='before')
    form(slot='form' @submit.prevent='search')
      .input-group
        label.label(for='recorder-search-by') Search By
        .inputs
          select-input#recorder-search-by(:options='searchByOptions' v-model='searchBy')

      template(v-if='searchBy === "documentParty"')
        app-input(
          label='Party Name'
          type='text'
          v-model='searchData.documentParty.fullName'
          :errors='errorMessages'
          :placeholder='nameSearchPlaceholder'
          required
        )
        super-search-input(:app-code='l10n("app.code")' v-model='searchData.documentParty.isDocumentSuperSearch')

      template(v-else-if='searchBy === "documentId"')
        app-input(
          label='Document Number'
          type='text'
          v-model='searchData.documentId.publicDocumentId'
          :errors='errorMessages'
          required
        )
        div(style='font-size: small;') example: 2003900156

      template(v-else)
        app-fieldset.horizontal(
          :legend='capitalize(l10n("terms.book")) + "/" +  capitalize(l10n("terms.page"))'
          :errors='bookPageErrors'
        )
          app-input._book_page_input(
            :label='capitalize(l10n("terms.book"))'
            v-model='searchData.bookAndPage.book'
          )
          app-input._book_page_input(
            :label='capitalize(l10n("terms.page"))'
            v-model='searchData.bookAndPage.page'
          )

      .input-group
        label.label(for='recorder-search-region') Search Region
        .inputs
          recorder-search-region-selector#recorder-search-region(
            :app-code='l10n("app.code")'
            v-model='searchData.shared.regionId'
            :read-only='readOnlyRegion'
          )
      template(v-if='$store.state.user.account.billingCodeEnabled')
        billing-code-input(v-model='searchData.shared.billingCode')
      .input-group
        .label
        .inputs
          button(type='submit' :disabled='isInvalid') Search
</template>

<script>
import AppInput from '@/components/app-input'
import AppFieldset from '@/components/app-fieldset'
import BillingCodeInput from '@/components/search/billing-code-input'
import InputField from '@/components/input-field'
import RecorderSearchRegionSelector from '@/components/search/recorder-search-region-selector'
import SearchWidget from '@/components/search/search-widget'
import SelectInput from '@/components/select-input'
import SuperSearchInput from '@/components/search/super-search-input'

import l10n from '@/assets/recorder/l10n'
import { isInvalid, errorMessages } from '@/assets/search-validation'

function restoreState (prop, defaultValue) {
  return (window.history.state || {})[prop] || defaultValue
}

export default {
  name: 'recorder-search-widget',
  components: {
    AppInput,
    AppFieldset,
    BillingCodeInput,
    InputField,
    RecorderSearchRegionSelector,
    SearchWidget,
    SelectInput,
    SuperSearchInput
  },
  props: {
    showCounts: {
      type: Boolean,
      required: false,
      default: false
    },
    showMap: {
      type: Boolean,
      required: false,
      default: true
    },
    regionId: {
      type: Number,
      default: null
    },
    readOnlyRegion: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      attemptedSearch: false,
      searchBy: restoreState('recorderSearchBy', 'documentParty'),
      bookPlaceHolder: this.capitalize(this.l10n('terms.book')),
      oldValue: '',
      searchByOptions: [
        { name: 'Document Party', value: 'documentParty' },
        { name: 'Document Number', value: 'documentId' },
        { name: this.capitalize(this.l10n('terms.book')) + ', Page', value: 'bookAndPage' }
      ],
      searchData: {
        documentParty: {
          fullName: restoreState('recorderFullName', ''),
          isDocumentSuperSearch: null
        },
        documentId: {
          publicDocumentId: restoreState('documentId', ''),
        },
        bookAndPage: {
          book: restoreState('book', ''),
          page: restoreState('page', ''),
        },
        shared: {
          regionId: this.regionId,
          billingCode: ''
        }
      }
    }
  },
  computed: {
    bookPageErrors () {
      if (this.missingInputError) {
        return { messages: [this.missingInputErrorMessage] }
      }
      return { messages: [] }
    },
    errorMessages () {
      let errors = errorMessages(this.searchData.documentParty.fullName, this.searchData.documentParty.isDocumentSuperSearch)
      if (this.missingInputError) (errors.messages).push(this.missingInputErrorMessage)
      return errors
    },
    isInvalid () {
      return isInvalid(this.searchData.documentParty.fullName, this.searchData.documentParty.isDocumentSuperSearch)
    },
    missingInput () {
      return ![
        this.searchData.documentParty.fullName,
        this.searchData.documentId.publicDocumentId,
        this.searchData.bookAndPage.book,
        this.searchData.bookAndPage.page
      ].some(e => !!e)
    },
    missingInputError () {
      return this.missingInput && this.attemptedSearch
    },

    missingInputErrorMessage () {
      if (this.missingInputError) {
        return {
          'documentParty': 'Please enter a Party Name.',
          'documentId': 'Please enter a Document Number.',
          'bookAndPage': `Please enter a ${this.capitalize(this.l10n('terms.book'))} and/or Page Number.`
        }[this.searchBy]
      }
      return null
    },
    nameSearchPlaceholder () {
      if (this.searchData.documentParty.isDocumentSuperSearch) return 'Party Name'
      return 'Last Name, First Name'
    }
  },
  watch: {
    missingInput (val) {
      if (!val) {
        this.attemptedSearch = false
      }
    },
    searchBy () {
      this.attemptedSearch = false
    }
  },
  methods: {
    capitalize (word) {
      if (!word) return word
      return word.charAt(0).toUpperCase() + word.substring(1)
    },
    l10n (key) {
      return l10n(this)(key)
    },
    search () {
      this.attemptedSearch = true
      if (this.missingInput) return

      var searchState = {
        recorderSearchBy: this.searchBy,
        recorderFullName: this.searchData.documentParty.fullName,
        documentId: this.searchData.documentId.publicDocumentId,
        book: this.searchData.bookAndPage.book,
        page: this.searchData.bookAndPage.page,
      }
      window.history.replaceState(searchState, null, null)

      this.$navigateTo({
        path: '/recorder/FindRecordedDocuments',
        query: {
          action: 'GET-FIND-RECORDED-DOCUMENTS',
          ...this.searchData.shared,
          ...this.searchData[this.searchBy]
        }
      })
    }
  }
}
</script>

<style lang="scss" module>
.book_page_group {
  display: flex !important;
  justify-content: space-between;
}
.book_page_input {
  flex: 0 1 49%;
}
.fieldset_container {
  margin-bottom: 1rem;
}
</style>
